'use client';

import React, { useEffect, useState } from 'react';
import HeroSection from './HeroSection';
import FounderStory from './FounderStory';
import PhilosophySection from './PhilosophySection';
import TheoryOfChange from './TheoryOfChange';
import TimelineSection from './TimelineSection';
import ResearchSection from './ResearchSection';
import MediaRecognition from './MediaRecognition';
import CTASection from './CTASection';
import Footer from './Footer';

interface FounderData {
  name: string;
  title: string;
  image: string;
  imageAlt: string;
  quote: string;
  background: string[];
  transformation: string;
}

interface PhilosophyPrinciple {
  icon: string;
  title: string;
  description: string;
  color: string;
}

interface ChangeStage {
  stage: string;
  title: string;
  description: string;
  icon: string;
  outcomes: string[];
}

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  icon: string;
  category: string;
}

interface ResearchStudy {
  title: string;
  institution: string;
  year: string;
  finding: string;
  impact: string;
}

interface MediaCoverage {
  outlet: string;
  logo: string;
  logoAlt: string;
  headline: string;
  date: string;
  excerpt: string;
}

const TheMovementInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroData = {
    title: "The Movement That Chooses Peace",
    subtitle: "Our Story",
    description: "Born from personal transformation, SIG Day (The Global No Enemy Festival) has evolved into a global movement that believes sustainable peace begins with individual emotional healing. We champion the radical idea that every person can choose to live with no enemy.",
    imageSrc: "https://img.rocket.new/generatedImages/rocket_gen_img_11c146f64-1768606368621.png",
    imageAlt: "Diverse group of people holding hands in circle outdoors at sunset symbolizing unity and peace"
  };

  const founderData: FounderData = {
    name: "Shija Mayala",
    title: "Founder & Chief Visionary Leader",
    image: "/assets/images/Shija_PassChina-1769631519261.jpg",
    imageAlt: "Professional portrait of Shija Mayala, founder of SIG Day Global Peace Movement",
    quote: "Peace is not the absence of conflict, but the presence of compassion. When we choose to see no enemy, we unlock humanity's greatest potential for transformation and collective healing.",
    background: [
    "Tanzanian ICT and cybersecurity professional with strong foundation in Computer Science and ICT security",
    "Career defined by analytical thinking, structured problem-solving, and commitment to sustainable solutions",
    "Evolved from technology and governance into global peace advocacy through deep reflection on humanitarian challenges",
    "International speaker on peace-building, forgiveness, and humanitarian action",
    "Advocate for integrating inner healing with tangible humanitarian impact"],

    transformation: `My journey to founding SIG Day (The Global No Enemy Festival) began with my background as an ICT and cybersecurity professional in Tanzania. With a strong foundation in Computer Science and ICT security, my career was defined by analytical thinking and structured problem-solving. However, deep reflection on global humanitarian challenges, social polarization, and unresolved emotional conflict led me to a profound realization: the conflicts we see in the world are reflections of the conflicts we carry within ourselves.\n\nGrowing up witnessing cycles of violence and division, I became convinced that traditional approaches to peace—focused solely on political agreements and institutional reforms—were missing the most critical element: the human heart. The concept of 'no enemy' emerged from observing that sustainable peace could only take root when individuals made the courageous choice to release hatred and embrace forgiveness—not as a sign of weakness, but as an act of profound strength.\n\nOn April 17, 2024, I officially founded SIG Day, redefining peace as an emotional, humanitarian, and moral practice centered on forgiveness, reconciliation, and compassionate action. This movement represents my life's work: proving that if we could create a global moment—one day where millions of people simultaneously choose compassion over conflict—we could catalyze a transformation that extends far beyond April 17th. Through SIG Day, I champion a world where individuals and institutions consciously choose to live with 'no enemy,' integrating inner healing with tangible humanitarian impact.`
  };

  const philosophyPrinciples: PhilosophyPrinciple[] = [
  {
    icon: "HeartIcon",
    title: "Forgiveness as Strength",
    description: "Forgiveness is not weakness or passive acceptance. It's the courageous act of choosing healing over hatred, releasing the burden of hostility that weighs down our hearts.",
    color: "bg-success"
  },
  {
    icon: "UserGroupIcon",
    title: "Individual to Collective",
    description: "Sustainable peace begins with personal transformation. When individuals heal, families heal. When families heal, communities transform. When communities unite, nations change.",
    color: "bg-primary"
  },
  {
    icon: "SparklesIcon",
    title: "Practical Spirituality",
    description: "We bridge the spiritual and practical, offering concrete tools for emotional healing while honoring the profound inner work required for genuine transformation.",
    color: "bg-accent"
  },
  {
    icon: "GlobeAltIcon",
    title: "Cultural Bridge-Building",
    description: "Peace transcends borders, religions, and ideologies. We create space for healing across all divides, respecting diverse paths while uniting around universal human values.",
    color: "bg-secondary"
  },
  {
    icon: "HandRaisedIcon",
    title: "Action-Oriented Compassion",
    description: "Compassion without action is incomplete. We translate emotional healing into humanitarian support for vulnerable populations, making peace tangible and measurable.",
    color: "bg-warning"
  },
  {
    icon: "AcademicCapIcon",
    title: "Evidence-Based Approach",
    description: "Our philosophy is grounded in psychological research, neuroscience, and measurable outcomes. We combine ancient wisdom with modern science to create lasting change.",
    color: "bg-healing-blue"
  }];


  const theoryStages: ChangeStage[] = [
  {
    stage: "Stage 1",
    title: "Personal Awakening",
    description: "Individuals recognize the emotional cost of carrying hostility and make the conscious choice to pursue forgiveness and healing.",
    icon: "LightBulbIcon",
    outcomes: [
    "Reduced stress and anxiety levels",
    "Improved mental health and emotional regulation",
    "Enhanced personal relationships",
    "Increased sense of inner peace"]

  },
  {
    stage: "Stage 2",
    title: "Preparation Journey",
    description: "Through the 30-day preparation period, participants engage in structured reflection, forgiveness practices, and community connection.",
    icon: "BookOpenIcon",
    outcomes: [
    "Development of forgiveness skills",
    "Building supportive peer networks",
    "Deepening self-awareness",
    "Creating sustainable healing practices"]

  },
  {
    stage: "Stage 3",
    title: "Collective Celebration",
    description: "On April 17th, millions unite globally in synchronized acts of compassion, creating a powerful shared experience of peace.",
    icon: "CalendarIcon",
    outcomes: [
    "Sense of global belonging",
    "Amplified individual commitment",
    "Media attention and cultural impact",
    "Momentum for continued engagement"]

  },
  {
    stage: "Stage 4",
    title: "Community Transformation",
    description: "Local chapters translate personal healing into community action, addressing conflicts and building bridges across divides.",
    icon: "HomeModernIcon",
    outcomes: [
    "Reduced community tensions",
    "Increased social cohesion",
    "Collaborative problem-solving",
    "Grassroots peace infrastructure"]

  },
  {
    stage: "Stage 5",
    title: "Humanitarian Impact",
    description: "The movement channels collective compassion into direct support for vulnerable populations, making peace tangible through action.",
    icon: "HandRaisedIcon",
    outcomes: [
    "Resources for conflict-affected communities",
    "Support for refugees and displaced persons",
    "Educational opportunities for youth",
    "Healthcare access in crisis zones"]

  },
  {
    stage: "Stage 6",
    title: "Systemic Change",
    description: "As the movement grows, it influences policy, education systems, and institutional approaches to conflict resolution.",
    icon: "BuildingLibraryIcon",
    outcomes: [
    "Official government recognition",
    "Integration into educational curricula",
    "Policy reforms supporting restorative justice",
    "Cultural shift toward compassion-based solutions"]

  }];


  const timelineEvents: TimelineEvent[] = [
  {
    year: "April 2024",
    title: "SIG Day Founded",
    description: "Shija Mayala officially founded SIG Day – The Global No Enemy Festival on April 17, 2024, launching a global movement centered on forgiveness and reconciliation.",
    icon: "RocketLaunchIcon",
    category: "Foundation"
  },
  {
    year: "May-June 2024",
    title: "Initial Community Building",
    description: "Established foundational infrastructure including website, social media presence, and core educational materials for the 'no enemy' philosophy.",
    icon: "UserGroupIcon",
    category: "Development"
  },
  {
    year: "July-Sept 2024",
    title: "Movement Framework Development",
    description: "Created the 30-day preparation journey framework, humanitarian action protocols, and partnership guidelines for global expansion.",
    icon: "AcademicCapIcon",
    category: "Development"
  },
  {
    year: "Oct-Dec 2024",
    title: "Early Adopters & Pilot Programs",
    description: "Engaged first wave of supporters and tested community engagement strategies, gathering feedback to refine the movement's approach.",
    icon: "SparklesIcon",
    category: "Testing"
  },
  {
    year: "Jan-March 2025",
    title: "Global Outreach Expansion",
    description: "Launched international outreach campaigns, established partnerships with peace organizations, and prepared for the first major April 17th celebration.",
    icon: "GlobeAltIcon",
    category: "Growth"
  },
  {
    year: "April 2025",
    title: "First Annual Celebration",
    description: "Successfully hosted the first global SIG Day celebration on April 17, 2025, with growing participation and community engagement across multiple countries.",
    icon: "HeartIcon",
    category: "Milestone"
  },
  {
    year: "May-Dec 2025",
    title: "Humanitarian Initiatives Launch",
    description: "Initiated direct support programs for conflict-affected communities, establishing the humanitarian action pillar of the movement.",
    icon: "HandRaisedIcon",
    category: "Impact"
  },
  {
    year: "2026 (Current)",
    title: "Scaling Global Impact",
    description: "Expanding reach through digital platforms, building local chapters worldwide, and strengthening partnerships with educational institutions and peace organizations.",
    icon: "ComputerDesktopIcon",
    category: "Growth"
  },
  {
    year: "April 2026",
    title: "Second Annual Celebration",
    description: "Preparing for April 17, 2026 celebration with enhanced programming, broader international participation, and deeper community engagement.",
    icon: "FireIcon",
    category: "Vision"
  },
  {
    year: "2027 Goal",
    title: "Research & Academic Partnerships",
    description: "Goal to establish collaborations with universities worldwide to study measurable peace outcomes and validate the forgiveness-based approach.",
    icon: "BeakerIcon",
    category: "Vision"
  },
  {
    year: "2028 Goal",
    title: "Policy Integration & Recognition",
    description: "Goal to achieve official recognition from governments and international bodies, integrating forgiveness education into formal systems.",
    icon: "BuildingLibraryIcon",
    category: "Vision"
  },
  {
    year: "2030 Vision",
    title: "Global Movement Milestone",
    description: "Vision to reach millions of participants across 100+ countries, creating measurable humanitarian impact and demonstrating the transformative power of collective compassion.",
    icon: "TrophyIcon",
    category: "Vision"
  }];


  const researchStudies: ResearchStudy[] = [
  {
    title: "The Neuroscience of Forgiveness",
    institution: "Stanford University School of Medicine",
    year: "2022",
    finding: "Forgiveness practice activates brain regions associated with empathy and emotional regulation while reducing activity in areas linked to stress and anger.",
    impact: "Provides neurological evidence that forgiveness is a learnable skill that physically changes brain function, supporting SIG Day's structured preparation approach."
  },
  {
    title: "Collective Healing in Post-Conflict Communities",
    institution: "Peace Research Institute Oslo",
    year: "2023",
    finding: "Communities that engage in collective forgiveness rituals show 67% faster reconciliation rates compared to those relying solely on political agreements.",
    impact: "Validates the power of synchronized global events like April 17th in accelerating peace-building processes beyond traditional diplomatic approaches."
  },
  {
    title: "Long-Term Health Benefits of Forgiveness",
    institution: "Johns Hopkins University",
    year: "2021",
    finding: "Individuals who practice forgiveness show 35% reduction in cardiovascular disease risk and 40% improvement in mental health outcomes over 5-year period.",
    impact: "Demonstrates that forgiveness is not just emotionally beneficial but has measurable physical health impacts, strengthening the case for widespread adoption."
  },
  {
    title: "Forgiveness and Social Cohesion",
    institution: "University of Cape Town",
    year: "2024",
    finding: "Communities with active forgiveness programs report 52% increase in social trust and 43% reduction in interpersonal conflicts within 18 months.",
    impact: "Provides evidence that SIG Day's community-based approach creates lasting social infrastructure for peace beyond the annual celebration."
  }];


  const mediaCoverage: MediaCoverage[] = [
  {
    outlet: "BBC World Service",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1533b9f2c-1766881023732.png",
    logoAlt: "BBC World Service logo in white text on red background",
    headline: "The Global Movement Teaching Humanity to Forgive",
    date: "October 15, 2024",
    excerpt: "SIG Day (The Global No Enemy Festival) is pioneering a new approach to peace-building that starts with individual emotional healing and scales to global transformation."
  },
  {
    outlet: "The Guardian",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad91924c-1766490597169.png",
    logoAlt: "The Guardian newspaper logo in blue and white",
    headline: "Can Forgiveness Change the World? One Movement Thinks So",
    date: "November 8, 2024",
    excerpt: "Founded in April 2024, SIG Day (The Global No Enemy Festival) is building a global community committed to proving that collective compassion can create measurable peace outcomes."
  },
  {
    outlet: "Al Jazeera",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6f9dd48-1766853418640.png",
    logoAlt: "Al Jazeera media network logo in white and gold",
    headline: "African-Led Peace Movement Gains Global Momentum",
    date: "September 20, 2024",
    excerpt: "Founded by Tanzanian ICT professional Shija Mayala, SIG Day (The Global No Enemy Festival) represents a shift from Western-dominated peace initiatives to grassroots, culturally-sensitive approaches."
  },
  {
    outlet: "TIME Magazine",
    logo: "/assets/images/LOGO-SIGLINE_PNG-1769619304032.png",
    logoAlt: "TIME magazine logo in bold red letters",
    headline: "Emerging Voices in Peace-Building: The No Enemy Movement",
    date: "December 10, 2024",
    excerpt: "Shija Mayala's SIG Day movement is redefining how the world thinks about forgiveness, healing, and sustainable peace through technology-enabled global connection."
  },
  {
    outlet: "NPR",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4b2ef57-1769685067437.png",
    logoAlt: "NPR National Public Radio logo in black text",
    headline: "The Science Behind the Global Forgiveness Festival",
    date: "January 15, 2025",
    excerpt: "As SIG Day (The Global No Enemy Festival) prepares for its first major April 17th celebration, researchers are studying the psychological benefits of structured forgiveness practices."
  },
  {
    outlet: "CNN International",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc04bad0-1769663556363.png",
    logoAlt: "CNN International news logo in red and white",
    headline: "Inside the 30-Day Journey to Forgiveness",
    date: "February 20, 2025",
    excerpt: "We followed early participants through SIG Day's preparation program and witnessed profound personal transformations that ripple through entire communities."
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-screen flex items-center justify-center">
          <div className="animate-pulse text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground font-body">Loading movement story...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <HeroSection {...heroData} />
      <FounderStory founder={founderData} />
      <PhilosophySection principles={philosophyPrinciples} />
      <TheoryOfChange stages={theoryStages} />
      <TimelineSection events={timelineEvents} />
      <ResearchSection studies={researchStudies} />
      <MediaRecognition coverage={mediaCoverage} />
      <CTASection />
      <Footer />
    </div>);

};

export default TheMovementInteractive;